import { useMemo } from 'react';
import { Table } from '@/components';
import { useSortableData } from '@/hooks';
import { transitControlTableColumns } from '../config/columns';
import { TransitControlItem } from '../models';

interface TransitControlTableProps {
  items: TransitControlItem[];
  isLoading?: boolean;
  totalItems?: number;
  totalPages?: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  dateTo?: Date | null;
}

const ONE_DAY_MS = 86400000;

const buildGetRowStyle = (dateTo?: Date | null) => {
  const referenceMoment = dateTo
    ? Math.min(Date.now(), dateTo.getTime())
    : Date.now();

  return (rowData: TransitControlItem): React.CSSProperties => {
    const referenceDate = rowData.eventDt || rowData.createdAt;
    if (!referenceDate) return {};
    const delayMs = referenceMoment - new Date(referenceDate).getTime();
    if (delayMs > ONE_DAY_MS) {
      return { backgroundColor: 'rgba(76, 175, 80, 0.15)' };
    }
    return {};
  };
};

const TransitControlTable = ({
  items,
  isLoading = false,
  totalItems = 0,
  totalPages = 0,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  dateTo,
}: TransitControlTableProps) => {
  const columns = useMemo(() => transitControlTableColumns, []);

  const { sortedData, selectedSort, handleSort } = useSortableData({
    data: items,
    currentPage,
  });

  const getRowStyle = useMemo(() => buildGetRowStyle(dateTo), [dateTo]);

  return (
    <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
      {isLoading || sortedData.length > 0 ? (
        <div className='overflow-x-auto'>
          <Table
            columns={columns}
            data={sortedData}
            selectedSort={selectedSort}
            handleSort={handleSort}
            isLoading={isLoading}
            getRowKey={(rowData) => String(rowData.number)}
            getRowStyle={getRowStyle}
            pagination={{
              totalItems,
              totalPages,
              currentPage,
              rowsPerPage: pageSize,
              onPageChange,
              onRowsPerPageChange: onPageSizeChange,
              rowsPerPageOptions: [50, 100, 150, 200],
            }}
          />
        </div>
      ) : (
        <div className='text-center py-8'>
          <p className='text-gray-600 text-lg'>No hay datos disponibles</p>
        </div>
      )}
    </div>
  );
};

export default TransitControlTable;
